import React, { useState, useCallback, useMemo } from 'react';
import styles from "./Form30Table.module.css";
import { FORM30_TABLES, TableConfig } from "../../../constants/form30Tables";

interface Form30TableProps {
    data: Record<string, Record<string, Record<string, number | null>>>;
    onDataChange: (tableId: string, rowId: string, columnKey: string, value: number | null) => void;
    isReadOnly?: boolean;
}

export const Form30Table: React.FC<Form30TableProps> = React.memo(({ data, onDataChange, isReadOnly = false }) => {
    const [activeTab, setActiveTab] = useState(FORM30_TABLES[0].id);

    const currentTable = useMemo(
        () => FORM30_TABLES.find(t => t.id === activeTab) || FORM30_TABLES[0],
        [activeTab]
    );

    const preventWheelChange = useCallback((e: React.WheelEvent<HTMLInputElement>) => {
        e.currentTarget.blur();
    }, []);

    // Функция для перехода к следующему инпуту
    const focusNextInput = useCallback((currentInput: HTMLInputElement) => {
        const allInputs = Array.from(document.querySelectorAll<HTMLInputElement>('input[type="number"]:not([disabled])'));
        const currentIndex = allInputs.indexOf(currentInput);
        
        if (currentIndex !== -1 && currentIndex < allInputs.length - 1) {
            const nextInput = allInputs[currentIndex + 1];
            nextInput.focus();
            nextInput.select(); // Выделяем текст для удобства
        }
    }, []);

    const renderTable = (table: TableConfig) => {
        const gridColumns = table.columns.map(col => col.width || '1fr').join(' ');
        
        // Для второй таблицы добавляем специальные строки шапки
        const isStructureTable = table.id === 'structure_of_diseases_1000';
        const isConsultationsTable = table.id === 'additional_consultations_2000';
        const isRehabilitationTable = table.id === 'rehabilitation_3000';
        const isPhysicalDevelopmentTable = table.id === 'physical_development_4000';
        const isPhysicalGroupsTable = table.id === 'physical_development_groups_5000';
        const isHealthGroupsTable = table.id === 'health_groups_6000';

        return (
            <div className={styles.table}>
                <div className={styles.tableBody}>
                    {isStructureTable && (
                        <>
                            {/* Первая строка шапки с объединенными ячейками */}
                            <div className={styles.rowHeadMerged} style={{gridTemplateColumns: gridColumns}}>
                                <div className={styles.colMerged} style={{gridColumn: '1 / 8'}}>
                                    Структура выявленных заболеваний (состояний) у детей в возрасте от 0 до 17 лет включительно
                                </div>
                                <div className={styles.colMerged} style={{gridColumn: '8 / 12'}}>
                                    Проводится диспансерное наблюдение на конец отчетного периода
                                </div>
                            </div>
                        </>
                    )}
                    {isConsultationsTable && (
                        <>
                            {/* Первая строка шапки - общее название */}
                            <div className={styles.rowHeadMerged} style={{gridTemplateColumns: gridColumns}}>
                                <div className={styles.colMerged} style={{gridColumn: '1 / 6'}}>
                                    Результаты консультаций, исследований, лечения, медицинской реабилитации детей по результатам проведения профилактических осмотров
                                </div>
                            </div>
                        </>
                    )}
                    {isRehabilitationTable && (
                        <>
                            {/* Первая строка шапки - общее название */}
                            <div className={styles.rowHeadMerged} style={{gridTemplateColumns: gridColumns}}>
                                <div className={styles.colMerged} style={{gridColumn: '1 / 7'}}>
                                    Лечение, медицинская реабилитация и санаторно-курортное лечение в 2025 году
                                </div>
                            </div>
                        </>
                    )}
                    {isPhysicalDevelopmentTable && (
                        <>
                            {/* Первая строка шапки с объединенными ячейками */}
                            <div className={styles.rowHeadMerged} style={{gridTemplateColumns: gridColumns}}>
                                <div className={styles.colMerged} style={{gridColumn: '1 / 4'}}>
                                    Число детей по уровню физического развития
                                </div>
                                <div className={styles.colMerged} style={{gridColumn: '4 / 8'}}>
                                    Нарушение физического развития (человек)
                                </div>
                            </div>
                        </>
                    )}
                    {isPhysicalGroupsTable && (
                        <>
                            {/* Первая строка шапки с объединенными ячейками */}
                            <div className={styles.rowHeadMerged} style={{gridTemplateColumns: gridColumns}}>
                                <div className={styles.colMerged} style={{gridColumn: '1 / 3'}}>
                                </div>
                                <div className={styles.colMerged} style={{gridColumn: '3 / 8'}}>
                                    По результатам ранее проведенных медицинских осмотров (человек)
                                </div>
                                <div className={styles.colMerged} style={{gridColumn: '8 / 13'}}>
                                    По результатам профилактических осмотров в данном отчетном периоде
                                </div>
                            </div>
                        </>
                    )}
                    {isHealthGroupsTable && (
                        <>
                            {/* Первая строка шапки с объединенными ячейками */}
                            <div className={styles.rowHeadMerged} style={{gridTemplateColumns: gridColumns}}>
                                <div className={styles.colMerged} style={{gridColumn: '1 / 3'}}>
                                </div>
                                <div className={styles.colMerged} style={{gridColumn: '3 / 8'}}>
                                    По результатам ранее проведенных медицинских осмотров (человек)
                                </div>
                                <div className={styles.colMerged} style={{gridColumn: '8 / 13'}}>
                                    По результатам профилактических осмотров в данном отчетном периоде
                                </div>
                            </div>
                        </>
                    )}
                    {/* Основная строка шапки с названиями колонок */}
                    <div className={styles.rowHead} style={{gridTemplateColumns: gridColumns}}>
                        {table.columns.map(col => {
                            const colClassName = col.wrap ? `${styles.col} ${styles.colWrap}` : styles.col;
                            return (
                                <div key={col.key} className={colClassName} title={col.label}>
                                    {col.label}
                                </div>
                            );
                        })}
                    </div>
                    {/* Строка с номерами колонок - последняя строка шапки */}
                    {(isStructureTable || isConsultationsTable || isRehabilitationTable || isPhysicalDevelopmentTable || isPhysicalGroupsTable || isHealthGroupsTable) && (
                        <div className={styles.rowHeadNumbers} style={{gridTemplateColumns: gridColumns}}>
                            {table.columns.map((col, index) => {
                                const colClassName = col.wrap ? `${styles.col} ${styles.colWrap}` : styles.col;
                                return (
                                    <div key={col.key} className={colClassName}>
                                        {index + 1}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                    {table.rows.map(row => {
                        // Если это строка-заголовок, объединяем все колонки в одну
                        if (row.isHeaderRow) {
                            return (
                                <div key={row.id} className={styles.row} style={{gridTemplateColumns: gridColumns}}>
                                    <div className={`${styles.col} ${styles.colWrap}`} style={{gridColumn: '1 / -1'}} title={row.indicator}>
                                        <strong>{row.number} {row.indicator}</strong>
                                    </div>
                                </div>
                            );
                        }
                        
                        // Обычная строка с данными
                        return (
                            <div key={row.id} className={styles.row} style={{gridTemplateColumns: gridColumns}}>
                                {table.columns.map(col => {
                                    const colClassName = col.wrap ? `${styles.col} ${styles.colWrap}` : styles.col;
                                    
                                    // Для текстовых колонок отображаем данные из конфигурации
                                    if (col.type === 'text') {
                                        if (col.key === 'number') {
                                            return <div key={col.key} className={colClassName} title={row.number}>{row.number}</div>;
                                        } else if (col.key === 'indicator' || col.key === 'disease_name' || col.key === 'age') {
                                            return <div key={col.key} className={colClassName} title={row.indicator}>{row.indicator}</div>;
                                        } else if (col.key === 'mkb_code') {
                                            return <div key={col.key} className={colClassName} title={row.mkbCode || ''}>{row.mkbCode || ''}</div>;
                                        }
                                    }
                                    
                                    // Для числовых колонок отображаем инпуты
                                    if (col.type === 'number') {
                                        return (
                                            <div key={col.key} className={colClassName}>
                                                <input
                                                    type="number"
                                                    onWheel={preventWheelChange}
                                                    onFocus={(e) => e.target.addEventListener('wheel', (evt) => evt.preventDefault(), { passive: false })}
                                                    min={0}
                                                    value={data[table.id]?.[row.id]?.[col.key] ?? ''}
                                                    onChange={(e) => {
                                                        const inputValue = e.target.value;
                                                        // Запрещаем ввод отрицательных значений
                                                        if (inputValue === '' || inputValue === '-') {
                                                            onDataChange(table.id, row.id, col.key, null);
                                                        } else {
                                                            const numValue = Number(inputValue);
                                                            if (numValue < 0) {
                                                                return; // Игнорируем отрицательные значения
                                                            }
                                                            onDataChange(table.id, row.id, col.key, numValue);
                                                        }
                                                    }}
                                                    onKeyDown={(e) => {
                                                        // Обработка Enter - переход к следующему инпуту
                                                        if (e.key === 'Enter') {
                                                            e.preventDefault();
                                                            focusNextInput(e.currentTarget);
                                                            return;
                                                        }
                                                        
                                                        // Запрещаем ввод всех нецифровых символов кроме управляющих клавиш
                                                        const allowedKeys = ['Backspace', 'Delete', 'Tab', 'Escape', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
                                                        const isNumber = /^[0-9]$/.test(e.key);
                                                        const isAllowed = allowedKeys.includes(e.key);
                                                        const isCtrlCmd = e.ctrlKey || e.metaKey; // Для Ctrl+C, Ctrl+V и т.д.
                                                        
                                                        if (!isNumber && !isAllowed && !isCtrlCmd) {
                                                            e.preventDefault();
                                                        }
                                                    }}
                                                    disabled={isReadOnly}
                                                />
                                            </div>
                                        );
                                    }
                                    
                                    return null;
                                })}
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    return (
        <div className={styles.tableWrapper}>
            <div className={styles.tabs}>
                {FORM30_TABLES.map(table => (
                    <button
                        key={table.id}
                        className={`${styles.tab} ${activeTab === table.id ? styles.tabActive : ''}`}
                        onClick={() => setActiveTab(table.id)}
                    >
                        {table.title}
                    </button>
                ))}
            </div>
            {renderTable(currentTable)}
        </div>
    );
});
